import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  getProcurementMitraFirstItem,
  getProcurementMitraId,
} from '../../../shared/offer-domain/respond';

const RESOLVE_CONCURRENCY = 3;

const resolveMitraIdForProcurement = async (procurementId, token) => {
  const response = await axios.get(
    `${api.API_URL}/procurement/${procurementId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    }
  );

  const payload = response?.data ?? response ?? {};
  const procurement = payload?.data ?? payload;
  const candidates = (procurement?.procurementMitra ?? []).filter(
    item => item?.id && item.isDeleted !== true
  );

  for (const candidate of candidates) {
    try {
      const mitraResponse = await axios.get(
        `${api.API_URL}/dmut/get-procurement-mitra/${candidate.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      const firstItem = getProcurementMitraFirstItem(mitraResponse);
      const resolvedId =
        (candidate?.id != null ? String(candidate.id) : null) ||
        getProcurementMitraId(firstItem) ||
        null;
      if (resolvedId) return resolvedId;
    } catch {
      continue;
    }
  }

  return candidates[0]?.id ?? null;
};

const mapWithConcurrency = async (items, limit, mapper) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(limit, items.length) || 0 },
    async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        try {
          results[index] = {
            status: 'fulfilled',
            value: await mapper(items[index], index),
          };
        } catch (reason) {
          results[index] = { status: 'rejected', reason };
        }
      }
    }
  );

  await Promise.all(workers);
  return results;
};

const useIcListProcurementMitraMap = ({
  procurementIds = [],
  seedMitraByProcurementId = null,
  enabled = true,
} = {}) => {
  const [resolvedMap, setResolvedMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const procurementIdsKey = useMemo(
    () =>
      [...new Set((procurementIds ?? []).filter(Boolean))]
        .map(String)
        .sort()
        .join(','),
    [procurementIds]
  );

  const seedKey = useMemo(() => {
    if (!seedMitraByProcurementId) return '';
    return Object.entries(seedMitraByProcurementId)
      .map(([procurementId, mitraId]) => `${procurementId}:${mitraId ?? ''}`)
      .sort()
      .join('|');
  }, [seedMitraByProcurementId]);

  useEffect(() => {
    if (!enabled || !procurementIdsKey) {
      setResolvedMap({});
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const token = getAuthToken();
      if (!token) {
        if (!cancelled) {
          setResolvedMap({});
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      const ids = procurementIdsKey.split(',').filter(Boolean);
      const missingIds = ids.filter(id => {
        const seeded =
          seedMitraByProcurementId?.[id] ??
          seedMitraByProcurementId?.[String(id)];
        return !seeded;
      });

      const next = {};
      ids.forEach(id => {
        const seeded =
          seedMitraByProcurementId?.[id] ??
          seedMitraByProcurementId?.[String(id)];
        if (seeded) next[id] = seeded;
      });

      if (missingIds.length) {
        const settled = await mapWithConcurrency(
          missingIds,
          RESOLVE_CONCURRENCY,
          async procurementId => {
            const mitraId = await resolveMitraIdForProcurement(
              procurementId,
              token
            );
            return [procurementId, mitraId];
          }
        );

        settled.forEach(result => {
          if (result?.status !== 'fulfilled') return;
          const [procurementId, mitraId] = result.value;
          if (mitraId) next[procurementId] = mitraId;
        });
      }

      if (!cancelled) {
        setResolvedMap(next);
        setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [enabled, procurementIdsKey, seedKey, seedMitraByProcurementId]);

  return { resolvedMap, isLoading };
};

export default useIcListProcurementMitraMap;
